import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useLazyQuery } from '@apollo/client';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import { getLabClientByIdTemplate } from 'apollo/templates/client-directory/query.graphql';
import { LabClient } from 'models/client-directory';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import DeliveryDates from '../delivery-step/delivery-dates/delivery-dates.component';
import LabOrderSchedulingSection from './lab-order-scheduling-section.component';
import { isValidDeliveryDate } from 'components/order-flow/shared/order-input-helper';

type ProductionPlanningStepProps = {
    onBackClick: () => void;
    onNextClick: () => void;
};

const ProductionPlanningStep: FC<ProductionPlanningStepProps> = ({ onBackClick, onNextClick }) => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order, setOrder } = useContext(LabOrderContext);
    const [clientSetting, setClientSetting] = useState<LabClient>({} as LabClient);
    const { showBoundary } = useDenthubErrorBoundary();

    const [getLabClientById] = useLazyQuery(getLabClientByIdTemplate, {
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ labClientById: client }: { labClientById: LabClient }) => {
            setClientSetting(client);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [isScheduleValid, setIsScheduleValid] = useState(true);

    // Only a new delivery clinic needs its client setting. Keyed on all of deliveryDetails, every
    // date or time edit refetched it, and the new setting reset the dates it came from (DD-3648).
    useEffect(() => {
        if (order.deliveryDetails?.destination?._id) {
            getLabClientById({ variables: { customerId: order.deliveryDetails.destination._id } });
        }
    }, [order.deliveryDetails?.destination?._id]);

    return (
        <Fragment>
            <Box mb={4}>
                <Typography variant="h2" mb={1}>
                    {translate('orders:DEADLINE')}
                </Typography>
                <DeliveryDates clientSetting={clientSetting} useOneRowLayout />
            </Box>
            <Box mb={4}>
                <Typography variant="h2" mb={1}>
                    {translate('orders:PRODUCTION_PLANNING')}
                </Typography>
                <Paper variant="outlined" sx={{ padding: 2 }}>
                    <LabOrderSchedulingSection onValidChange={setIsScheduleValid} />
                </Paper>
            </Box>
            <Box display="flex" mt={2}>
                <Box flexGrow={1} textAlign="left">
                    <Button
                        type="button"
                        onClick={() => {
                            setOrder((prev) => ({ ...prev, productionPlanningSteps: [] }));
                            onBackClick();
                        }}
                        data-test="production-planning-step.back"
                    >
                        {translate('common:BACK')}
                    </Button>
                </Box>
                <Box>
                    <Button
                        disabled={!isValidDeliveryDate(order.deliveryDetails.date) || !isScheduleValid}
                        type="button"
                        onClick={onNextClick}
                        variant="contained"
                        data-test="production-planning-step.next"
                    >
                        {translate('common:NEXT_STEP')}
                    </Button>
                </Box>
            </Box>
        </Fragment>
    );
};

export default ProductionPlanningStep;
